import { Avatar, AvatarFallback, AvatarImage } from '@rocco/components/avatar';

interface ConsultantDisplayProps {
    meetingType: 'one-on-one' | 'random';
    consultantName: string;
    consultantSubtitle: string | null;
    consultantImage: string | null;
    consultantLogoUrl: string | null;
    consultantInitial: string;
    consultantLogoBackgroundColor: string | undefined;
    professionalTitle?: string;
    profileUrlSegment: string;
    professionalCategories?: string[];
    isProfessionalLoading: boolean;
}

/**
 * Displays consultant/professional information for booking
 * Shows different layouts for one-on-one vs random meeting types
 */
export const ConsultantDisplay = ({
    meetingType,
    consultantName,
    consultantSubtitle,
    consultantImage,
    consultantLogoUrl,
    consultantInitial,
    consultantLogoBackgroundColor,
    professionalCategories = [],
    isProfessionalLoading,
    professionalTitle,
    profileUrlSegment,
}: ConsultantDisplayProps) => {
    const handleProfileClick = () => {
        if (profileUrlSegment) {
            window.open(`/professional/${profileUrlSegment}`, '_blank');
        }
    };
    return (
        <div
            className="w-90 transition-opacity duration-150"
            style={{ opacity: isProfessionalLoading ? 0 : 1 }}
        >
            {meetingType === 'one-on-one' ? (
                <div className="overflow-hidden">
                    <div className="mx-auto h-100 w-87 overflow-hidden rounded-3 bg-gray-100">
                        {consultantImage ? (
                            <img
                                className="h-full w-full object-cover"
                                src={consultantImage}
                                alt={consultantName}
                            />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center text-10 font-semibold text-gray-500">
                                {consultantInitial}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-4 py-5">
                        <Avatar
                            size="square40"
                            border="light"
                            className="h-15 w-15 cursor-pointer"
                            onClick={handleProfileClick}
                        >
                            {consultantLogoUrl ? (
                                <AvatarImage
                                    src={consultantLogoUrl}
                                    alt={`${professionalTitle} logo`}
                                    fit="contain"
                                />
                            ) : (
                                <AvatarFallback>
                                    {professionalTitle?.charAt(0)}
                                </AvatarFallback>
                            )}
                        </Avatar>
                        <div>
                            <div className="cursor-pointer text-base font-semibold text-gray-900">
                                {consultantName}
                            </div>
                            {consultantSubtitle ? (
                                <div className="mt-1 text-sm text-gray-500">
                                    {consultantSubtitle}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div
                        className="relative mt-4 h-68 w-94 cursor-pointer"
                        onClick={handleProfileClick}
                    >
                        <div className="absolute top-0 left-0 h-full w-full overflow-hidden rounded-3">
                            {consultantImage ? (
                                <img
                                    className="h-68 w-94 rounded-3 object-cover"
                                    src={consultantImage}
                                    alt={consultantName}
                                />
                            ) : (
                                <div className="flex h-66 w-90 items-center justify-center rounded-3 bg-gray-100 text-base font-semibold text-gray-500">
                                    {consultantInitial}
                                </div>
                            )}
                        </div>
                        <div
                            className="absolute top-4 left-4 flex h-18 w-18 items-center justify-center overflow-hidden rounded-full"
                            style={{
                                backgroundColor:
                                    consultantLogoBackgroundColor ?? 'white',
                            }}
                        >
                            {consultantLogoUrl ? (
                                <img
                                    className="h-14 w-14 object-contain"
                                    src={consultantLogoUrl}
                                    alt={`${consultantName} logo`}
                                />
                            ) : (
                                <div className="flex h-full w-full items-center justify-center text-sm font-semibold text-gray-500">
                                    {consultantInitial}
                                </div>
                            )}
                        </div>
                    </div>
                    <h3
                        className="mt-6 cursor-pointer text-base font-semibold text-gray-900"
                        onClick={handleProfileClick}
                    >
                        {professionalTitle}
                    </h3>
                    <p className="mt-1 text-base text-gray-500">
                        {professionalCategories[0] || ''}
                    </p>
                </>
            )}
        </div>
    );
};
